import {v4 as uuidv4} from 'uuid';
import {CommandType, UndoableCommand} from '../interfaces/command.interface';
import {generateUndoLabel, UndoLabelMetadata} from '../undo-label.utils';

/**
 * CompositeCommand combines multiple commands into a single atomic operation.
 * Useful for operations like "cut" which is copy + delete.
 *
 * Commands are executed in order and undone in reverse order.
 */
export class CompositeCommand implements UndoableCommand {
  public readonly id: string;
  public readonly timestamp: number;
  public readonly type: CommandType;
  public readonly description: string;
  public readonly labelMetadata?: UndoLabelMetadata;

  private commands: UndoableCommand[];

  constructor(
    type: CommandType,
    description: string,
    commands: UndoableCommand[],
    labelMetadata?: UndoLabelMetadata
  ) {
    this.id = uuidv4();
    this.timestamp = Date.now();
    this.type = type;
    this.labelMetadata = labelMetadata;
    // If labelMetadata is provided, generate description from it; otherwise use the provided description
    this.description = labelMetadata ? generateUndoLabel(labelMetadata) : description;
    this.commands = commands;
  }

  async execute(): Promise<void> {
    for (const command of this.commands) {
      await command.execute();
    }
  }

  async undo(): Promise<void> {
    // Undo in reverse order
    for (let i = this.commands.length - 1; i >= 0; i--) {
      await this.commands[i].undo();
    }
  }

  getCommands(): UndoableCommand[] {
    return [...this.commands];
  }
}
